/**
 * User Management Data Table Component
 * V4.162 - Updated for granular permission system
 * Displays permission chips instead of role badges
 */

import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  DeleteForever as DeleteIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { AdminUser, Permission } from '../../types/auth';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/uiSlice';
import { AppDispatch } from '../../store';
import apiService from '../../services/api';
import EditPermissionsModal from './EditPermissionsModal';
import { countFullAccess, countViewOnly, mapLegacyRoleToPermissions } from '../../config/permissionResources';

interface UserTableProps {
  users: AdminUser[];
  onRefresh: () => void;
  currentUserId?: string;
}

export const UserTable: React.FC<UserTableProps> = ({ users, onRefresh, currentUserId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPermissionsModal = (user: AdminUser) => {
    // Ensure user has permissions array (handle legacy data)
    const userWithPermissions = {
      ...user,
      permissions: user.permissions?.length > 0
        ? user.permissions
        : mapLegacyRoleToPermissions(user.role || user.legacy_role || 'view_only'),
    };
    setSelectedUser(userWithPermissions);
    setPermissionsModalOpen(true);
    handleMenuClose();
  };

  const handleSavePermissions = async (userId: string, permissions: Permission[]) => {
    setLoading(true);
    try {
      await apiService.updateUserPermissions(userId, permissions);
      dispatch(addNotification({
        type: 'success',
        message: 'User permissions updated successfully',
      }));
      onRefresh();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update permissions',
      }));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    if (userId === currentUserId) {
      dispatch(addNotification({
        type: 'warning',
        message: 'You cannot deactivate your own account',
      }));
      return;
    }

    setLoading(true);
    try {
      await apiService.updateUserStatus(userId, !currentStatus);
      dispatch(addNotification({
        type: 'success',
        message: `User ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
      }));
      onRefresh();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update user status',
      }));
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await apiService.deleteAdminUser(selectedUser.id);
      dispatch(addNotification({ type: 'success', message: `Admin user "${selectedUser.name || selectedUser.email}" deleted successfully` }));
      onRefresh();
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to delete admin user' }));
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  // Get permission counts for display
  const getPermissionCounts = (user: AdminUser) => {
    // If user has computed counts from API, use those
    if (user.full_access_count !== undefined && user.view_only_count !== undefined) {
      return {
        full: user.full_access_count,
        viewOnly: user.view_only_count,
        total: user.permission_count || (user.full_access_count + user.view_only_count),
      };
    }

    // Otherwise calculate from permissions array
    const permissions = user.permissions || [];
    return {
      full: countFullAccess(permissions),
      viewOnly: countViewOnly(permissions),
      total: permissions.filter(p => p.access_level !== 'none').length,
    };
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'User',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Avatar
            src={params.row.avatar_url}
            alt={params.row.name}
            sx={{ width: 40, height: 40 }}
          >
            {params.row.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {params.row.name}
              {params.row.id === currentUserId && (
                <Chip size="small" label="You" color="primary" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {params.row.email}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'permissions',
      headerName: 'Permissions',
      width: 200,
      renderCell: (params: GridRenderCellParams<AdminUser>) => {
        const counts = getPermissionCounts(params.row);

        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {counts.full > 0 && (
              <Tooltip title={`${counts.full} resources with full access`}>
                <Chip
                  size="small"
                  icon={<SecurityIcon sx={{ fontSize: 14 }} />}
                  label={`${counts.full} Full`}
                  sx={{
                    bgcolor: '#10B98120',
                    color: '#10B981',
                    border: '1px solid #10B98140',
                    '& .MuiChip-icon': { color: '#10B981' },
                  }}
                />
              </Tooltip>
            )}
            {counts.viewOnly > 0 && (
              <Tooltip title={`${counts.viewOnly} resources with view-only access`}>
                <Chip
                  size="small"
                  icon={<ViewIcon sx={{ fontSize: 14 }} />}
                  label={`${counts.viewOnly} View`}
                  sx={{
                    bgcolor: '#6366F120',
                    color: '#6366F1',
                    border: '1px solid #6366F140',
                    '& .MuiChip-icon': { color: '#6366F1' },
                  }}
                />
              </Tooltip>
            )}
            {counts.total === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No permissions
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'default'}
          variant={params.row.is_active ? 'filled' : 'outlined'}
          size="small"
          icon={params.row.is_active ? <CheckCircleIcon /> : <BlockIcon />}
        />
      ),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 180,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Tooltip
            title={params.row.last_login ? format(new Date(params.row.last_login), 'PPpp') : 'Never'}
          >
            <Typography variant="body2" color="text.secondary">
              {params.row.last_login
                ? formatDistanceToNow(new Date(params.row.last_login), { addSuffix: true })
                : 'Never'}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params: GridRenderCellParams<AdminUser>) => (
        <Tooltip title={format(new Date(params.row.created_at), 'PPpp')}>
          <Typography variant="body2" color="text.secondary">
            {format(new Date(params.row.created_at), 'MMM dd, yyyy')}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          key="menu"
          icon={
            <IconButton size="small">
              <MoreVertIcon />
            </IconButton>
          }
          label="More actions"
          onClick={(event) => handleMenuClick(event as React.MouseEvent<HTMLElement>, params.row)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell': {
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => selectedUser && handleOpenPermissionsModal(selectedUser)}
          disabled={selectedUser?.id === currentUserId}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Permissions
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleStatusToggle(selectedUser.id, selectedUser.is_active)}
          disabled={selectedUser?.id === currentUserId}
        >
          {selectedUser?.is_active ? (
            <>
              <BlockIcon sx={{ mr: 1 }} fontSize="small" />
              Deactivate User
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
              Activate User
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }}
          disabled={selectedUser?.id === currentUserId}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Admin
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Admin User?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{selectedUser?.name || selectedUser?.email}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Modal */}
      <EditPermissionsModal
        open={permissionsModalOpen}
        user={selectedUser}
        onClose={() => {
          setPermissionsModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSavePermissions}
      />
    </Box>
  );
};

export default UserTable;
